import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Line, Pie } from "react-chartjs-2";

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Tooltip,
  Legend,
  Filler
} from "chart.js";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Tooltip,
  Legend,
  Filler
);

const API = "https://tulithevlogger.com/api/orders";

const AdminDashboard = () => {
  const [orders, setOrders] = useState([]);
  const [filtered, setFiltered] = useState([]);

  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const token = localStorage.getItem("token");

  // =========================
  // 🔥 SAFE HELPERS
  // =========================
  const toNumber = (val) => {
    const num = Number(val);
    return isNaN(num) ? 0 : num;
  };

  const getDate = (o) => {
    const raw = o.createdat || o.CreatedAt;
    if (!raw) return null;

    const d = new Date(raw);
    return isNaN(d) ? null : d;
  };

  const formatCurrency = (val) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR"
    }).format(val || 0);
  };

  const getStatus = (o) => o.status || o.Status || "";

  // =========================
  // LOAD DATA
  // =========================
  const loadOrders = useCallback(async () => {
    try {
      const res = await axios.get(API, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const data = res.data.data || res.data;
      setOrders(data);
      setFiltered(data);
    } catch (err) {
      console.error("Load error:", err);
    }
  }, [token]);

  useEffect(() => {
    loadOrders();
    const interval = setInterval(loadOrders, 10000);
    return () => clearInterval(interval);
  }, [loadOrders]);

  // =========================
  // FILTER
  // =========================
  useEffect(() => {
    let data = [...orders];

    if (fromDate) {
      data = data.filter(o => {
        const d = getDate(o);
        return d && d >= new Date(fromDate);
      });
    }

    if (toDate) {
      data = data.filter(o => {
        const d = getDate(o);
        return d && d <= new Date(toDate);
      });
    }

    setFiltered(data);
  }, [fromDate, toDate, orders]);

  // =========================
  // CALCULATIONS
  // =========================
  const totalOrders = filtered.length;

  const revenue = filtered.reduce(
    (sum, o) => sum + toNumber(o.totalamount || o.TotalAmount),
    0
  );

  const profit = revenue * 0.3;

  const pending = filtered.filter(o => getStatus(o) === "Pending").length;
  const delivered = filtered.filter(o => getStatus(o) === "Delivered").length;

  // =========================
  // GROUPING
  // =========================
  const ordersByDate = {};
  const revenueByDate = {};
  const productSales = {};

  filtered.forEach(o => {
    const d = getDate(o);
    if (!d) return;

    const date = d.toISOString().slice(0, 10);

    ordersByDate[date] = (ordersByDate[date] || 0) + 1;
    revenueByDate[date] =
      (revenueByDate[date] || 0) +
      toNumber(o.totalamount || o.TotalAmount);

    o.items?.forEach(item => {
      const name = item.ProductName || item.name;
      const qty = toNumber(item.Quantity || item.quantity);

      productSales[name] = (productSales[name] || 0) + qty;
    });
  });

  const labels = Object.keys(ordersByDate);

  const topProducts = Object.entries(productSales)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5);

  const ranking = Object.entries(productSales)
    .sort((a, b) => b[1] - a[1]);

  // =========================
  // CHART DATA
  // =========================
  const ordersChart = {
    labels,
    datasets: [
      {
        label: "Orders",
        data: Object.values(ordersByDate),
        borderColor: "#007bff",
        backgroundColor: "rgba(0,123,255,0.15)",
        fill: true,
        tension: 0.4
      }
    ]
  };

  const revenueChart = {
    labels,
    datasets: [
      {
        label: "Revenue",
        data: Object.values(revenueByDate),
        borderColor: "#28a745",
        backgroundColor: "rgba(40,167,69,0.15)",
        fill: true,
        tension: 0.4
      }
    ]
  };

  const pieChart = {
    labels: ["Pending", "Delivered"],
    datasets: [
      {
        data: [pending, delivered],
        backgroundColor: ["#ffc107", "#28a745"]
      }
    ]
  };

  // =========================
  // UI
  // =========================
  return (
    <div style={styles.page}>
      <h2 style={styles.title}>📊 Admin Dashboard</h2>

      {/* FILTER */}
      <div style={styles.filter}>
        <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} />
        <input type="date" value={toDate} onChange={e => setToDate(e.target.value)} />
      </div>

      {/* CARDS */}
      <div style={styles.cards}>
        <Card title="Orders" value={totalOrders} />
        <Card title="Revenue" value={formatCurrency(revenue)} />
        <Card title="Profit" value={formatCurrency(profit)} />
        <Card title="Pending" value={pending} />
        <Card title="Delivered" value={delivered} />
      </div>

      {/* CHARTS */}
      <div style={styles.chartGrid}>
        <div style={styles.box}>
          <h4>Orders Trend</h4>
          <Line data={ordersChart} />
        </div>

        <div style={styles.box}>
          <h4>Revenue Trend</h4>
          <Line data={revenueChart} />
        </div>

        <div style={styles.box}>
          <h4>Status</h4>
          <Pie data={pieChart} />
        </div>
      </div>

      {/* TOP PRODUCTS */}
      <div style={styles.box}>
        <h4>🏆 Top Products</h4>
        {topProducts.map((p, i) => (
          <div key={i} style={styles.row}>
            <span>{p[0]}</span>
            <span>{p[1]}</span>
          </div>
        ))}
      </div>

      {/* RANKING */}
      <div style={styles.box}>
        <h4>📊 Sales Ranking</h4>
        <table style={styles.table}>
          <thead>
            <tr>
              <th>#</th>
              <th>Product</th>
              <th>Sold</th>
            </tr>
          </thead>
          <tbody>
            {ranking.map((p, i) => (
              <tr key={i}>
                <td>{i + 1}</td>
                <td>{p[0]}</td>
                <td>{p[1]}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminDashboard;

// =========================
// CARD
// =========================
const Card = ({ title, value }) => (
  <div style={styles.card}>
    <div style={styles.cardTitle}>{title}</div>
    <div style={styles.cardValue}>{value}</div>
  </div>
);

// =========================
// STYLES
// =========================
const styles = {
  page: { padding: 20, background: "#f5f7fb" },
  title: { marginBottom: 15 },

  filter: {
    display: "flex",
    gap: 10,
    marginBottom: 15
  },

  cards: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
    gap: 15
  },

  card: {
    background: "#fff",
    padding: 15,
    borderRadius: 8,
    textAlign: "center",
    border: "1px solid #eee"
  },

  cardTitle: {
    fontSize: 13,
    color: "#777"
  },

  cardValue: {
    fontSize: 20,
    fontWeight: "bold",
    marginTop: 5
  },

  chartGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
    gap: 20,
    marginTop: 20
  },

  box: {
    background: "#fff",
    padding: 15,
    borderRadius: 8,
    border: "1px solid #eee"
  },

  row: {
    display: "flex",
    justifyContent: "space-between",
    padding: "6px 0",
    borderBottom: "1px solid #f0f0f0"
  },

  table: {
    width: "100%",
    borderCollapse: "collapse",
    marginTop: 10
  }
};